/**
 * Deutsche Diktat-Nachbearbeitung.
 *
 * WARUM: Die Spracherkennung liefert eine Wortkette ohne Satzzeichen —
 * „die brandwand ist tragend punkt neue zeile nachweis nach baybo“. In einem
 * Brandschutznachweis ist so ein Text unbrauchbar. Diese Datei setzt die
 * gesprochenen Satzzeichen und die Groß-/Kleinschreibung am Satzanfang.
 *
 * Bewusst reine Funktionen ohne Browser-Bezug: dadurch ohne Web Speech API
 * testbar (diktat.spec.ts) und für einen späteren serverseitigen Erkenner
 * genauso verwendbar.
 *
 * GRENZE, die bleibt: „Punkt“ ist auch ein normales Wort („Punkt 3 der
 * Anlage“). Wir ersetzen es trotzdem — ohne Satzzeichen-Befehle ist Diktat
 * nutzlos, und der Nutzer kann im Feld nachbessern. Dass ein Wort verschwindet,
 * ist im Text sofort sichtbar; ein fehlender Punkt fällt niemandem auf.
 */

/** Satzzeichen-Befehle. Mehrwortbefehle werden vor Einwortbefehlen geprüft. */
const ZEICHEN_BEFEHLE: Record<string, string> = {
  punkt: '.',
  komma: ',',
  fragezeichen: '?',
  ausrufezeichen: '!',
  doppelpunkt: ':',
  semikolon: ';',
  strichpunkt: ';',
  bindestrich: '-',
  schrägstrich: '/',
  prozent: '%',
  paragraf: '§',
  paragraph: '§',
  'klammer auf': '(',
  'klammer zu': ')',
  'anführungszeichen auf': '„',
  'anführungszeichen zu': '“',
  absatz: '\n\n',
  'neuer absatz': '\n\n',
  'neue zeile': '\n',
  zeilenumbruch: '\n',
  'punkt punkt punkt': '…',
};

/** Höchste Wortanzahl eines Befehls — begrenzt das Nachschauen im Text. */
const MAX_BEFEHLSWOERTER = 3;

/** Klebt ohne Leerzeichen an das vorherige Wort. */
const ANHAENGEND = new Set(['.', ',', '?', '!', ':', ';', ')', '“', '…', '%']);
/** Klebt ohne Leerzeichen an das folgende Wort. */
const OEFFNEND = new Set(['(', '„', '§']);
/** Verbindet beide Seiten ohne Leerzeichen. */
const VERBINDEND = new Set(['-', '/']);

type TeilArt = 'start' | 'wort' | 'anhaengend' | 'oeffnend' | 'verbindend' | 'umbruch';

/** Kleinschreiben und Satzzeichen abstreifen — nur für den Befehlsvergleich. */
function befehlsform(wort: string): string {
  return wort.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

function artVonZeichen(zeichen: string): TeilArt {
  if (zeichen.startsWith('\n')) return 'umbruch';
  if (ANHAENGEND.has(zeichen)) return 'anhaengend';
  if (OEFFNEND.has(zeichen)) return 'oeffnend';
  if (VERBINDEND.has(zeichen)) return 'verbindend';
  return 'wort';
}

/** Braucht vor diesem Teil ein Leerzeichen, gemessen am vorherigen Teil? */
function brauchtLeerzeichen(vorher: TeilArt, jetzt: TeilArt): boolean {
  if (vorher === 'start' || vorher === 'umbruch') return false;
  if (vorher === 'oeffnend' || vorher === 'verbindend') return false;
  if (jetzt === 'anhaengend' || jetzt === 'verbindend' || jetzt === 'umbruch') return false;
  return true;
}

export interface DiktatOptionen {
  /**
   * Beginnt der Text einen neuen Satz? Dann wird das erste Wort großgeschrieben.
   * Beim Weiterdiktieren mitten im Feld ist das falsch — deshalb abschaltbar.
   */
  satzanfang?: boolean;
}

/**
 * Wandelt eine erkannte Wortkette in schreibfertigen deutschen Text:
 * gesprochene Satzzeichen werden gesetzt, Abstände korrigiert und Satzanfänge
 * großgeschrieben.
 */
export function wandleDiktat(roh: string, optionen: DiktatOptionen = {}): string {
  const woerter = roh.trim().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return '';

  let ergebnis = '';
  let letzteArt: TeilArt = 'start';

  const schreibe = (text: string, art: TeilArt): void => {
    if (brauchtLeerzeichen(letzteArt, art)) ergebnis += ' ';
    ergebnis += text;
    letzteArt = art;
  };

  for (let i = 0; i < woerter.length; i += 1) {
    // Längste Wortfolge zuerst: „neuer absatz“ schlägt „absatz“.
    let treffer: { zeichen: string; laenge: number } | null = null;
    for (let laenge = Math.min(MAX_BEFEHLSWOERTER, woerter.length - i); laenge >= 1; laenge -= 1) {
      const folge = woerter
        .slice(i, i + laenge)
        .map(befehlsform)
        .join(' ');
      const zeichen = ZEICHEN_BEFEHLE[folge];
      if (zeichen !== undefined) {
        treffer = { zeichen, laenge };
        break;
      }
    }

    if (treffer) {
      schreibe(treffer.zeichen, artVonZeichen(treffer.zeichen));
      i += treffer.laenge - 1;
      continue;
    }

    schreibe(woerter[i]!, 'wort');
  }

  return grossSchreibenNachSatzende(ergebnis, optionen.satzanfang ?? true);
}

/**
 * Großschreibung am Satzanfang. Alles Weitere überlassen wir dem Erkenner —
 * deutsche Substantive rät man nicht zuverlässig, und falsch Großgeschriebenes
 * ist schlimmer als gar nichts.
 */
function grossSchreibenNachSatzende(text: string, auchAmAnfang: boolean): string {
  let ergebnis = text.replace(
    /([.!?…]\s+|\n+)(\p{Ll})/gu,
    (_treffer, davor: string, buchstabe: string) => davor + buchstabe.toUpperCase(),
  );
  if (auchAmAnfang) {
    ergebnis = ergebnis.replace(/^(\p{Ll})/u, (buchstabe) => buchstabe.toUpperCase());
  }
  return ergebnis;
}

/**
 * Fügt frisch Diktiertes in einen bestehenden Feldwert ein.
 *
 * Entscheidet anhand des vorhandenen Textes, ob ein neuer Satz beginnt — sonst
 * würde jedes Weiterdiktieren mitten im Satz ein Großbuchstabe erzeugen.
 */
export function diktatEinfuegen(bestehend: string, roh: string): string {
  const links = bestehend.replace(/[ \t]+$/, '');
  const neuerSatz = links.trim() === '' || /[.!?…]$/.test(links) || links.endsWith('\n');
  const zusatz = wandleDiktat(roh, { satzanfang: neuerSatz });
  if (zusatz === '') return bestehend;
  if (links === '') return zusatz;
  if (links.endsWith('\n')) return links + zusatz;
  // Satzzeichen des neuen Bausteins nicht durch ein Leerzeichen abtrennen.
  if (/^[.,;:!?…]/.test(zusatz)) return links + zusatz;
  return `${links} ${zusatz}`;
}

/** Nur für die Hilfe-Anzeige: welche Satzzeichen-Befehle es gibt. */
export const DIKTAT_BEFEHLE_HILFE: { gesprochen: string; ergibt: string }[] = [
  { gesprochen: 'Punkt', ergibt: '.' },
  { gesprochen: 'Komma', ergibt: ',' },
  { gesprochen: 'Fragezeichen', ergibt: '?' },
  { gesprochen: 'Doppelpunkt', ergibt: ':' },
  { gesprochen: 'Klammer auf / Klammer zu', ergibt: '( )' },
  { gesprochen: 'Bindestrich', ergibt: '-' },
  { gesprochen: 'Paragraf', ergibt: '§' },
  { gesprochen: 'neue Zeile', ergibt: 'Zeilenumbruch' },
  { gesprochen: 'neuer Absatz', ergibt: 'Leerzeile' },
];
